import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  formatDate,
  formatCurrency,
  formatCNPJ,
  formatarValorTabela,
} from '@/lib/utils/formatters';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Pagination } from '@/components/shared/Pagination';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Filter, Columns } from 'lucide-react';
import { useHeader } from '@/contexts/HeaderContext';
import { useListaDocumentosItens } from '@/services/Documentos';
import { ItensNotaSkeleton } from './ItensNotaSkeleton';
import { TabEventos } from './TabEventos';
import { CreditoIbsCbsDaNota } from './CreditoIbsCbsDaNota';
import { BotaoBaixarXml } from './BotaoBaixarXml';
import {
  COLUNAS_DISPONIVEIS,
  getModeloNome,
  getModelo,
  getModeloKey,
  criarPayloadItens,
  calcularTotalPaginas,
  toggleColunaVisibilidade,
  toggleGrupoColunas,
  MODELOS_COM_EVENTOS,
} from './documentos-helpers';
import type { Documento, ItemDocumento } from '@/services/Documentos';

interface DetalhesNotaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documento: Documento | null;
  tipo: '0' | '1';
}

export function DetalhesNotaModal({ open, onOpenChange, documento, tipo }: DetalhesNotaModalProps) {
  const { filtros: filtrosHeader } = useHeader();
  const [filtrosOpen, setFiltrosOpen] = useState(false);
  const [colunasOpen, setColunasOpen] = useState(false);
  const [cfop, setCfop] = useState('');
  const [cst, setCst] = useState('');
  const [cest, setCest] = useState('');
  const [cClasstrib, setCClasstrib] = useState('');
  const [produtoCodigo, setProdutoCodigo] = useState('');
  const [produtoDescricao, setProdutoDescricao] = useState('');
  const [produtoEan, setProdutoEan] = useState('');
  const [produtoNcm, setProdutoNcm] = useState('');

  const [colunasVisiveis, setColunasVisiveis] = useState<string[]>(
    COLUNAS_DISPONIVEIS.map(col => col.id)
  );

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);

  useEffect(() => {
    if (open && documento) {
      setPaginaAtual(1);
      setCfop('');
      setCst('');
      setCest('');
      setCClasstrib('');
      setProdutoCodigo('');
      setProdutoDescricao('');
      setProdutoEan('');
      setProdutoNcm('');
    }
  }, [open, documento, filtrosHeader.dataInicio, filtrosHeader.dataFim]);

  const modeloDocumento = documento ? getModelo(documento.ChaveAcessoDOC) : '55';
  const possuiItensProdutos = ['55', '65', '59'].includes(modeloDocumento);
  const possuiEventos = MODELOS_COM_EVENTOS.has(modeloDocumento);
  const isNfe = modeloDocumento === '55';
  const valorIbs = documento
    ? (parseFloat(documento.vIBSUF || '0') || 0) +
      (parseFloat(documento.vIBSMun || '0') || 0) +
      (parseFloat(documento.vIBSMon || '0') || 0)
    : 0;
  const statusDescricao =
    documento?.StatusDescricao === 'Homologado' ? 'Autorizado' : documento?.StatusDescricao;

  useEffect(() => {
    if (!isNfe) {
      setCst('');
      setCest('');
    }
  }, [isNfe]);

  const payload = documento
    ? criarPayloadItens({
        paginaAtual,
        itensPorPagina,
        dataInicio: filtrosHeader.dataInicio,
        dataFim: filtrosHeader.dataFim,
        chaveAcesso: documento.ChaveAcessoDOC,
        tipoDocumento: tipo,
        modelo: modeloDocumento,
        cnpjContribuinte: documento.CNPJ_Contribuinte,
        cfop,
        cst: isNfe ? cst : undefined,
        cest: isNfe ? cest : undefined,
        cclasstrib: cClasstrib,
        produtoCodigo,
        produtoDescricao,
        produtoEan,
        produtoNcm,
        dataEmissao: documento.DataEmissao,
      })
    : criarPayloadItens({
        paginaAtual: 1,
        itensPorPagina: 10,
        dataInicio: null,
        dataFim: null,
        chaveAcesso: '',
        tipoDocumento: tipo,
        modelo: '55',
        cnpjContribuinte: '',
      });

  const {
    data,
    isLoading,
    error: _error,
  } = useListaDocumentosItens(payload, {
    enabled: open && !!documento && possuiItensProdutos,
  });

  const modeloKey = documento ? getModeloKey(modeloDocumento) : 'nfe';
  const dadosModelo = data?.documents[modeloKey];
  const itens = dadosModelo?.items || [];
  const totalItens = dadosModelo?.totalItens || 0;
  const totalPaginas = calcularTotalPaginas(totalItens, itensPorPagina);

  const toggleColuna = (colunaId: string) => {
    setColunasVisiveis(prev => toggleColunaVisibilidade(prev, colunaId));
  };

  const toggleGrupo = (grupo: string) => {
    setColunasVisiveis(prev => toggleGrupoColunas(prev, grupo));
  };

  const renderizarValorColuna = (item: ItemDocumento, colunaId: string): React.ReactNode => {
    switch (colunaId) {
      case 'Sequencial':
        return item.Sequencial;
      case 'Produto':
        return item.Produto;
      case 'Descricao':
        return item.Descricao;
      case 'EAN':
        return item.EAN || '';
      case 'NCM':
        return item.NCM || '';
      case 'CFOP':
        return item.CFOP || '';
      case 'CEST':
        return item.CEST || '';
      case 'Unidade':
        return item.Unidade || '';
      case 'Qtde':
        return formatarValorTabela(item.Qtde, 'numero');
      case 'ValorUnitario':
        return formatarValorTabela(item.ValTot / item.Qtde, 'moeda');
      case 'ValTot':
        return formatarValorTabela(item.ValTot, 'moeda');
      case 'CST':
        return item.CST || '';
      case 'ICMS':
        return formatarValorTabela(item.ICMS, 'percentual');
      case 'ValorICMS':
        return formatarValorTabela(item.ValorICMS, 'moeda');
      case 'ValorBaseICMS':
        return formatarValorTabela(item.ValorBaseICMS, 'moeda');
      case 'RedBC':
        return formatarValorTabela(item.RedBC, 'percentual');
      case 'FCP':
        return formatarValorTabela(item.FCP, 'moeda');
      case 'ICMSUFDest':
        return formatarValorTabela(item.ICMSUFDest, 'moeda');
      case 'ICMSST':
        return formatarValorTabela(item.ICMSST, 'percentual');
      case 'ValorICMSST':
        return formatarValorTabela(item.ValorICMSST, 'moeda');
      case 'ValorBaseICMSST':
        return formatarValorTabela(item.ValorBaseICMSST, 'moeda');
      case 'MVAST':
        return formatarValorTabela(item.MVAST, 'percentual');
      case 'RedBCST':
        return formatarValorTabela(item.RedBCST, 'percentual');
      case 'FCPST':
        return formatarValorTabela(item.FCPST, 'moeda');
      case 'CSTIpi':
        return item.CSTIpi || '';
      case 'IPIAliq':
        return formatarValorTabela(item.IPIAliq, 'percentual');
      case 'ValorIPI':
        return formatarValorTabela(item.ValorIPI, 'moeda');
      case 'CSTPis':
        return item.CSTPis || '';
      case 'PISAliq':
        return formatarValorTabela(item.PISAliq, 'percentual');
      case 'ValorPIS':
        return formatarValorTabela(item.ValorPIS, 'moeda');
      case 'VL_BC_PIS':
        return formatarValorTabela(item.VL_BC_PIS, 'moeda');
      case 'CSTCofins':
        return item.CSTCofins || '';
      case 'COFINSAliq':
        return formatarValorTabela(item.COFINSAliq, 'percentual');
      case 'ValorCOFINS':
        return formatarValorTabela(item.ValorCOFINS, 'moeda');
      case 'VL_BC_COFINS':
        return formatarValorTabela(item.VL_BC_COFINS, 'moeda');
      case 'ValDesc':
        return formatarValorTabela(item.ValDesc, 'moeda');
      case 'vRatAcr':
        return formatarValorTabela(item.vRatAcr, 'moeda');
      case 'vRatDesc':
        return formatarValorTabela(item.vRatDesc, 'moeda');
      case 'vFrete':
        return formatarValorTabela(item.vFrete, 'moeda');
      case 'vSeg':
        return formatarValorTabela(item.vSeg, 'moeda');
      case 'vOUtro':
        return formatarValorTabela(item.vOUtro, 'moeda');
      case 'cBenef':
        return item.cBenef || '';
      case 'cClassTrib':
        return item.cClassTrib || '';
      case 'vIBS':
        return formatarValorTabela(item.vIBS, 'moeda');
      case 'vCBS':
        return formatarValorTabela(item.vCBS, 'moeda');
      default: {
        const valor = (item as unknown as Record<string, unknown>)[colunaId];
        if (typeof valor === 'number') {
          return colunaId.startsWith('v') || colunaId.includes('Valor') || colunaId.includes('Base')
            ? formatarValorTabela(valor, 'moeda')
            : colunaId.startsWith('p') || colunaId.includes('Aliq')
              ? formatarValorTabela(valor, 'percentual')
              : formatarValorTabela(valor, 'numero');
        }
        return formatarValorTabela(valor, 'texto');
      }
    }
  };

  if (!documento) return null;

  const isEntrada = tipo === '0';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none! w-[80vw] max-h-[95vh] p-0 flex flex-col overflow-hidden [&>button]:z-20 [&>button]:top-6 [&>button]:right-8 [&>button]:size-6 [&>button>svg]:size-6">
        <DialogHeader className="sticky top-0 z-10 bg-background border-b px-8 py-6 rounded-t-lg">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-2xl">
              DANFE - Documento Auxiliar da Nota Fiscal Eletrônica
            </DialogTitle>
            <Badge variant={isEntrada ? 'default' : 'destructive'} className="text-sm">
              {isEntrada ? 'ENTRADA' : 'SAÍDA'}
            </Badge>
          </div>
          <DialogDescription className="text-base">
            {`${getModeloNome(documento.ChaveAcessoDOC)} ${documento.Numero} - Série ${documento.Serie}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-8 overflow-y-auto flex-1 px-8 py-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {/* Identificação */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Identificação</CardTitle>
                <BotaoBaixarXml chaveAcesso={documento.ChaveAcessoDOC} />
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Número</p>
                <p className="font-medium">{documento.Numero}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Série</p>
                <p className="font-medium">{documento.Serie}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Data de Emissão</p>
                <p className="font-medium">{formatDate(documento.DataEmissao)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Modelo</p>
                <p className="font-medium">{getModeloNome(documento.ChaveAcessoDOC)}</p>
              </div>
              <div className="col-span-4">
                <p className="text-muted-foreground">Chave de Acesso</p>
                <p className="font-mono text-xs break-all">{documento.ChaveAcessoDOC}</p>
              </div>
            </CardContent>
          </Card>

          {/* Contribuinte e Fornecedor/Cliente */}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contribuinte</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <div>
                  <p className="text-muted-foreground">CNPJ</p>
                  <p className="font-medium">{formatCNPJ(documento.CNPJ_Contribuinte)}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{isEntrada ? 'Fornecedor' : 'Cliente'}</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">CNPJ</p>
                  <p className="font-medium">{formatCNPJ(documento.CNPJ)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Razão Social</p>
                  <p className="font-medium">{documento.RazaoSocial}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">UF</p>
                  <p className="font-medium">{documento.UF}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Itens da Nota Fiscal (apenas NF-e 55, NFC-e 65 e SAT 59) */}
          {possuiItensProdutos && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Itens da Nota Fiscal</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setColunasOpen(true)}>
                      <Columns className="mr-2 h-4 w-4" />
                      Colunas ({colunasVisiveis.length})
                    </Button>

                    <Button variant="outline" size="sm" onClick={() => setFiltrosOpen(true)}>
                      <Filter className="mr-2 h-4 w-4" />
                      Filtros
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <ItensNotaSkeleton />
                ) : itens.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum item encontrado
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader className="bg-card">
                          <TableRow className="hover:bg-card">
                            {colunasVisiveis.map((colunaId, index) => {
                              const coluna = COLUNAS_DISPONIVEIS.find(c => c.id === colunaId);
                              if (!coluna) return null;

                              const isFirstColumn = index === 0;
                              const isNumeric =
                                [
                                  'Qtde',
                                  'ValorUnitario',
                                  'ValTot',
                                  'ValorICMS',
                                  'ValorICMSST',
                                  'ValorIPI',
                                  'ValorPIS',
                                  'ValorCOFINS',
                                  'ValorBaseICMS',
                                  'ValorBaseICMSST',
                                  'ValDesc',
                                  'vFrete',
                                  'vSeg',
                                  'vOUtro',
                                  'vIBS',
                                  'vCBS',
                                ].includes(colunaId) ||
                                colunaId.startsWith('v') ||
                                colunaId.includes('Valor') ||
                                colunaId.includes('Base');

                              const minWidth = colunaId === 'Descricao' ? '250px' : '100px';

                              return (
                                <TableHead
                                  key={colunaId}
                                  style={{ minWidth }}
                                  className={`${
                                    isFirstColumn
                                      ? 'sticky left-0 bg-card z-10 w-16 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]'
                                      : ''
                                  } ${isNumeric ? 'text-right' : ''}`}
                                >
                                  {coluna.label}
                                </TableHead>
                              );
                            })}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {itens.map((item: ItemDocumento) => (
                            <TableRow key={item.Sequencial}>
                              {colunasVisiveis.map((colunaId, index) => {
                                const isFirstColumn = index === 0;
                                const isNumeric =
                                  [
                                    'Qtde',
                                    'ValorUnitario',
                                    'ValTot',
                                    'ValorICMS',
                                    'ValorICMSST',
                                    'ValorIPI',
                                    'ValorPIS',
                                    'ValorCOFINS',
                                    'ValorBaseICMS',
                                    'ValorBaseICMSST',
                                    'ValDesc',
                                    'vFrete',
                                    'vSeg',
                                    'vOUtro',
                                    'vIBS',
                                    'vCBS',
                                  ].includes(colunaId) ||
                                  colunaId.startsWith('v') ||
                                  colunaId.includes('Valor') ||
                                  colunaId.includes('Base');

                                return (
                                  <TableCell
                                    key={colunaId}
                                    className={`${
                                      isFirstColumn
                                        ? 'font-medium sticky left-0 bg-card group-hover:bg-muted/50 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] transition-colors'
                                        : ''
                                    } ${isNumeric ? 'text-right' : ''} ${
                                      colunaId === 'Descricao' ? 'max-w-[250px]' : ''
                                    } ${
                                      [
                                        'Produto',
                                        'EAN',
                                        'NCM',
                                        'CFOP',
                                        'CEST',
                                        'CST',
                                        'CSTPis',
                                        'CSTCofins',
                                        'CSTIpi',
                                      ].includes(colunaId)
                                        ? 'font-mono text-xs'
                                        : ''
                                    }`}
                                  >
                                    {colunaId === 'Descricao' ? (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div className="truncate cursor-default">
                                              {renderizarValorColuna(item, colunaId)}
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="max-w-md">
                                            <p>{item.Descricao}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ) : (
                                      renderizarValorColuna(item, colunaId)
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="flex items-center py-4 border-t mt-4">
                      <Pagination
                        currentPage={paginaAtual}
                        totalPages={totalPaginas}
                        onPageChange={setPaginaAtual}
                        itensPorPagina={itensPorPagina}
                        onItensPorPaginaChange={novosItens => {
                          setItensPorPagina(novosItens);
                          setPaginaAtual(1);
                        }}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Valores */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Valores</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-6 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Valor Total NF</p>
                <p className="font-bold text-lg">
                  {formatCurrency(parseFloat(documento.vNF || '0'))}
                </p>
              </div>
              {documento.vProd && (
                <div>
                  <p className="text-muted-foreground">Valor Produtos</p>
                  <p className="font-medium">{formatCurrency(parseFloat(documento.vProd))}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">ICMS</p>
                <p className="font-medium">{formatCurrency(parseFloat(documento.vICMS || '0'))}</p>
              </div>
              <div>
                <p className="text-muted-foreground">ICMS ST</p>
                <p className="font-medium">
                  {formatCurrency(parseFloat(documento.vICMSST || '0'))}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">IPI</p>
                <p className="font-medium">{formatCurrency(parseFloat(documento.vIPI || '0'))}</p>
              </div>
              <div>
                <p className="text-muted-foreground">PIS</p>
                <p className="font-medium">{formatCurrency(parseFloat(documento.vPIS || '0'))}</p>
              </div>
              <div>
                <p className="text-muted-foreground">COFINS</p>
                <p className="font-medium">
                  {formatCurrency(parseFloat(documento.vCOFINS || '0'))}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Reforma Tributária - IBS e CBS */}
          {(documento.vIBSUF !== undefined ||
            documento.vIBSMun !== undefined ||
            documento.vIBSMon !== undefined ||
            documento.vCBS) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Reforma Tributária - IBS e CBS</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-4 gap-4 text-sm">
                {(documento.vIBSUF !== undefined ||
                  documento.vIBSMun !== undefined ||
                  documento.vIBSMon !== undefined) && (
                  <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                    <p className="text-muted-foreground mb-2">
                      IBS <span className="text-xs">(0,1%)</span>
                    </p>
                    <p className="font-bold text-lg text-orange-600">{formatCurrency(valorIbs)}</p>
                  </div>
                )}
                {documento.vCBS && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                    <p className="text-muted-foreground mb-2">
                      CBS <span className="text-xs">(0,9%)</span>
                    </p>
                    <p className="font-bold text-lg text-purple-600">
                      {formatCurrency(parseFloat(documento.vCBS))}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {isNfe && tipo === '0' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Crédito de IBS e CBS na entrada</CardTitle>
              </CardHeader>
              <CardContent>
                <CreditoIbsCbsDaNota chaveAcesso={documento.ChaveAcessoDOC} />
              </CardContent>
            </Card>
          )}

          {/* Eventos (manifestação do destinatário — NF-e, NFC-e e CT-e) */}
          {possuiEventos && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Eventos</CardTitle>
              </CardHeader>
              <CardContent>
                <TabEventos chaveAcesso={documento.ChaveAcessoDOC} codigoModelo={modeloDocumento} />
              </CardContent>
            </Card>
          )}

          {/* Informações Adicionais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informações Adicionais</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Status</p>
                <Badge variant={statusDescricao === 'Autorizado' ? 'default' : 'secondary'}>
                  {statusDescricao}
                </Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Protocolo SEFAZ</p>
                <p className="font-medium">{documento.ProtocoloSefaz || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Data/Hora Gravação</p>
                <p className="font-medium">{formatDate(documento.dataHoraGravacao)}</p>
              </div>
              {documento.Divergencia && (
                <div>
                  <p className="text-muted-foreground">Divergência</p>
                  <Badge variant={documento.Divergencia === 'S' ? 'destructive' : 'secondary'}>
                    {documento.Divergencia === 'S' ? 'Sim' : 'Não'}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>

      {possuiItensProdutos && (
        <>
          {/* Modal de Filtros Personalizados dos Itens */}
          <Dialog open={filtrosOpen} onOpenChange={setFiltrosOpen}>
            <DialogContent className="max-w-2xl p-8">
              <DialogHeader>
                <DialogTitle>Filtros Personalizados</DialogTitle>
                <DialogDescription>
                  Filtre os itens da nota fiscal por critérios específicos
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-2">
                <div className={`flex flex-col gap-2 ${isNfe ? 'col-span-1' : 'col-span-2'}`}>
                  <Label>CFOP</Label>
                  <Input
                    value={cfop}
                    onChange={e => setCfop(e.target.value)}
                    placeholder="Ex: 1102"
                    className="w-full h-10"
                  />
                </div>

                {isNfe && (
                  <div className="flex flex-col gap-2 col-span-1">
                    <Label>CST</Label>
                    <Input
                      value={cst}
                      onChange={e => setCst(e.target.value)}
                      placeholder="Ex: 60"
                      className="w-full h-10"
                    />
                  </div>
                )}

                {isNfe && (
                  <div className="flex flex-col gap-2 col-span-1">
                    <Label>CEST</Label>
                    <Input
                      value={cest}
                      onChange={e => setCest(e.target.value)}
                      placeholder="Ex: 2002800"
                      className="w-full h-10"
                    />
                  </div>
                )}

                <div className={`flex flex-col gap-2 ${isNfe ? 'col-span-1' : 'col-span-2'}`}>
                  <Label>NCM</Label>
                  <Input
                    value={produtoNcm}
                    onChange={e => setProdutoNcm(e.target.value)}
                    placeholder="Ex: 33072010"
                    className="w-full h-10"
                  />
                </div>

                <div className="flex flex-col gap-2 col-span-1">
                  <Label>Código do Produto</Label>
                  <Input
                    value={produtoCodigo}
                    onChange={e => setProdutoCodigo(e.target.value)}
                    placeholder="Ex: 242621"
                    className="w-full h-10"
                  />
                </div>

                <div className="flex flex-col gap-2 col-span-1">
                  <Label>EAN</Label>
                  <Input
                    value={produtoEan}
                    onChange={e => setProdutoEan(e.target.value)}
                    placeholder="Ex: 7898663111859"
                    className="w-full h-10"
                  />
                </div>

                <div className="flex flex-col gap-2 col-span-2">
                  <Label>Descrição do Produto</Label>
                  <Input
                    value={produtoDescricao}
                    onChange={e => setProdutoDescricao(e.target.value)}
                    placeholder="Ex: DESOD.GIOVANNA B.AERO..."
                    className="w-full h-10"
                  />
                </div>

                <div className="flex flex-col gap-2 col-span-2">
                  <Label>cClasstrib</Label>
                  <Input
                    value={cClasstrib}
                    onChange={e => setCClasstrib(e.target.value)}
                    placeholder="Ex: 000001"
                    className="w-full h-10"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setFiltrosOpen(false)}>
                  Fechar
                </Button>
                <Button
                  onClick={() => {
                    setPaginaAtual(1);
                    setFiltrosOpen(false);
                  }}
                >
                  Aplicar Filtros
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog de Configuração de Colunas */}
          <Dialog open={colunasOpen} onOpenChange={setColunasOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Configurar Colunas</DialogTitle>
                <DialogDescription>
                  Selecione quais colunas deseja visualizar na tabela
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-hidden">
                <ScrollArea className="h-[500px] pr-4">
                  {Object.entries(
                    COLUNAS_DISPONIVEIS.reduce(
                      (acc, col) => {
                        if (!acc[col.grupo]) acc[col.grupo] = [];
                        acc[col.grupo].push(col);
                        return acc;
                      },
                      {} as Record<string, typeof COLUNAS_DISPONIVEIS>
                    )
                  ).map(([grupo, colunas]) => (
                    <div key={grupo} className="mb-6">
                      <div className="flex items-center space-x-2 mb-3">
                        <Checkbox
                          id={`grupo-${grupo}`}
                          checked={colunas.every(c => colunasVisiveis.includes(c.id))}
                          onCheckedChange={() => toggleGrupo(grupo)}
                        />
                        <label
                          htmlFor={`grupo-${grupo}`}
                          className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {grupo}
                        </label>
                      </div>
                      <div className="ml-6 space-y-2">
                        {colunas.map(coluna => (
                          <div key={coluna.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`col-${coluna.id}`}
                              checked={colunasVisiveis.includes(coluna.id)}
                              onCheckedChange={() => toggleColuna(coluna.id)}
                            />
                            <label
                              htmlFor={`col-${coluna.id}`}
                              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {coluna.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              </div>
              <div className="flex justify-between items-center gap-2 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setColunasVisiveis(COLUNAS_DISPONIVEIS.map(col => col.id));
                  }}
                >
                  Selecionar Todas
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setColunasOpen(false)}>
                    Fechar
                  </Button>
                  <Button onClick={() => setColunasOpen(false)}>Aplicar</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </Dialog>
  );
}
